import React, { useState } from 'react';

import { c, msgid } from 'ttag';

import { ChevronDown } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { PermissionsQuery } from '../../graphql/__generated__/types';
import { PortalModuleRef, buildRightsGroups } from './grouping';

type Permission = NonNullable<NonNullable<PermissionsQuery['permissions']>[number]>;

interface Props {
    /** The whole referential, so the rights a role does **not** grant are visible too. */
    referential: Permission[];
    /** The module referential: what names and orders the groups. */
    modules: PortalModuleRef[];
    /** Codes this role grants. */
    granted: Set<string>;
}

/**
 * Rights of a role, over the **whole** referential rather than only the ones granted.
 * A list of what a role allows cannot be read without what it does not: « Signature
 * absente » is the answer to the question someone opened this screen with.
 *
 * The shape is the mockup's: one collapsible bordered block per group, its header
 * carrying the name and a granted/total counter, its body one line per right with a
 * checkbox and the code underneath. The checkboxes are **disabled** rather than
 * replaced by icons: the mockup does the same, and a disabled checkbox says « this
 * is a state you are reading, not a control you may use » better than any tooltip.
 *
 * **Attention, grouped by module since PCG-99**, not by capability. See `grouping.ts` for why,
 * and for where the labels and the order come from.
 *
 * **Attention, `delivered` annotates the group and never disables a right.** Granting a right on
 * an undelivered module is legitimate and the server accepts it: disabling the box
 * would refuse what the API allows. What the annotation buys is that « Factures, 5
 * droits » stops reading exactly like « Mes besoins, 6 droits » when none of those five
 * can take effect.
 *
 * Read-only in this story. Editing would write `role_permission` rows against a
 * referential whose taxonomy has just moved, and reading costs nothing where writing
 * makes data to migrate.
 */
const RightsMatrix = ({ referential, modules, granted }: Props) => {
    const groups = buildRightsGroups(referential, modules);

    // The dependency is the referential's (`requiresPermissionCode`), read through this
    // map to show the *name* of the right required rather than its code. The mockup
    // hard-codes the same rule in a `SEE_OF` constant: a second writing of it is the
    // one that drifts, so there is none here.
    const labelByCode = new Map(referential.map((permission) => [permission.code ?? '', permission.label ?? '']));

    /**
     * Les sections OUVERTES, et non les repliees : la matrice s'ouvre fermee
     * depuis le 2026-08-13, sur demande de Mohamed.
     *
     * Attention, ce sens defait un choix argumente : le commentaire d'origine
     * disait « ouvertes par defaut, comme la maquette : une matrice qui s'ouvre
     * fermee cache exactement ce que l'ecran existe pour montrer ». L'argument
     * valait pour treize droits ; ils sont trente-six depuis PCG-99, repartis en
     * sept groupes, et tout deplier donne un mur qu'il faut faire defiler pour
     * atteindre le groupe qu'on cherchait. Fermee, la matrice montre d'abord SA
     * STRUCTURE, qui est ce que le lecteur parcourt en premier.
     *
     * Attention, ET C'EST L'ENSEMBLE OUVERT QU'ON STOCKE, jamais l'ensemble
     * replie. Un Set vide vaut alors « tout ferme », qui est le defaut voulu :
     * l'autre sens aurait demande d'initialiser l'etat avec toutes les cles, or
     * les groupes arrivent par requete et le premier rendu n'en a AUCUN.
     * L'initialisation serait passee a cote, silencieusement, et la matrice se
     * serait ouverte deployee.
     */
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const toggle = (key: string) =>
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });

    const total = referential.filter((permission) => granted.has(permission.code ?? '')).length;

    return (
        <div className="flex flex-col gap-2">
            {groups.map((group) => {
                const grantedHere = group.permissions.filter((permission) => granted.has(permission.code ?? ''));
                const isOpen = expanded.has(group.key);

                return (
                    <section
                        key={group.key}
                        className={clsx(
                            'overflow-hidden rounded-md border',
                            group.anomaly ? 'border-warning-500' : 'border-border-norm'
                        )}
                    >
                        <h3>
                            <button
                                type="button"
                                onClick={() => toggle(group.key)}
                                aria-expanded={isOpen}
                                className="flex w-full items-center gap-2.5 bg-background-weak px-3 py-2.5 text-left"
                            >
                                <ChevronDown
                                    size={12}
                                    className={clsx(
                                        'shrink-0 text-text-500 transition-transform',
                                        !isOpen && '-rotate-90'
                                    )}
                                />
                                <span className="text-sm font-semibold text-text-900">{group.label}</span>
                                {group.delivered === false && (
                                    <span className="rounded-full border border-border-norm bg-background-norm px-2 py-0.5 text-[11px] font-medium text-text-600">
                                        {c('Rights matrix').t`Screen not delivered yet`}
                                    </span>
                                )}
                                {group.anomaly && (
                                    <span className="rounded-full border border-warning-500 px-2 py-0.5 text-[11px] font-medium text-warning-700">
                                        {c('Rights matrix').t`Unexpected`}
                                    </span>
                                )}
                                <span className="ml-auto shrink-0 rounded-full border border-border-norm bg-background-norm px-2 py-0.5 font-mono text-xs text-text-600">
                                    {grantedHere.length}/{group.permissions.length}
                                </span>
                            </button>
                        </h3>

                        {isOpen && (
                            <ul className="border-t border-border-norm px-3 py-1.5">
                                {/* Can only happen on a module of the referential that no right
                                    hangs off yet. Said, rather than left as an open empty block. */}
                                {group.permissions.length === 0 && (
                                    <li className="py-1.5 text-sm text-text-500">
                                        {c('Rights matrix').t`No right attached to this module yet.`}
                                    </li>
                                )}
                                {group.permissions.map((permission) => {
                                    const isGranted = granted.has(permission.code ?? '');
                                    // The API already nulls the field on a right that depends on
                                    // nothing; the self-comparison guards the sentinel the
                                    // database stores underneath it.
                                    const requiredCode =
                                        permission.requiresPermissionCode &&
                                        permission.requiresPermissionCode !== permission.code
                                            ? permission.requiresPermissionCode
                                            : null;
                                    const requiredLabel = requiredCode
                                        ? labelByCode.get(requiredCode) || requiredCode
                                        : null;

                                    return (
                                        <li key={permission.code} className="flex items-start gap-2.5 py-1.5">
                                            <input
                                                type="checkbox"
                                                checked={isGranted}
                                                disabled
                                                readOnly
                                                // The state is already carried by the box and the
                                                // label; the name says which right it belongs to,
                                                // heard on its own.
                                                aria-label={permission.label ?? permission.code ?? ''}
                                                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-admin-accent"
                                            />
                                            <span className="min-w-0">
                                                <span
                                                    className={clsx(
                                                        'block text-sm',
                                                        isGranted ? 'text-text-900' : 'text-text-500'
                                                    )}
                                                >
                                                    {permission.label}
                                                </span>
                                                <span className="block font-mono text-[11px] text-text-500">
                                                    {permission.code}
                                                </span>
                                                {permission.description && (
                                                    <span className="block text-xs text-text-500">
                                                        {permission.description}
                                                    </span>
                                                )}
                                                {requiredLabel && (
                                                    <span className="block text-xs text-text-500">
                                                        {c('Rights matrix').t`Requires: ${requiredLabel}`}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                );
            })}

            {/* A chip and not a line of text, as the mockup closes its matrix. */}
            <p className="mt-1 self-start rounded-full border border-border-norm bg-background-weak px-2.5 py-1 font-mono text-xs text-text-600">
                {total === 0
                    ? c('Rights matrix').t`No right granted`
                    : c('Rights matrix').ngettext(msgid`${total} right granted`, `${total} rights granted`, total)}
            </p>
        </div>
    );
};

export default RightsMatrix;
